import { imageUrlForKey } from "../stitch-bind";
import { formatPriceCop } from "../price-format";
import { buildWhatsAppProductUrl } from "../whatsapp";
import { homeFeaturedProductSlugs } from "../content/home-featured";
import { categoryLabelEs } from "../content/spec-rows";
import { products } from "../content/products";
import type { Product } from "../content/types";

function productsInFeaturedOrder(): Product[] {
  const map = new Map(products.map((p) => [p.slug, p] as const));
  const list: Product[] = [];
  for (const slug of homeFeaturedProductSlugs) {
    const p = map.get(slug);
    if (p !== undefined) list.push(p);
  }
  return list;
}

function categoriesInFeatured(items: readonly Product[]): Product["category"][] {
  const set = new Set<Product["category"]>();
  for (const p of items) set.add(p.category);
  return Array.from(set).sort((a, b) => categoryLabelEs(a).localeCompare(categoryLabelEs(b), "es"));
}

export function renderHomeFeaturedProducts(): void {
  const root = document.querySelector("[data-home-featured-mount]");
  if (!(root instanceof HTMLElement)) return;

  const allFeatured = productsInFeaturedOrder();
  if (allFeatured.length === 0) return;

  root.textContent = "";

  const head = document.createElement("div");
  head.className = "home-featured__head";

  const titleWrap = document.createElement("div");
  const eyebrow = document.createElement("span");
  eyebrow.className = "home-eyebrow";
  eyebrow.textContent = "Selección actual";
  const h2 = document.createElement("h2");
  h2.className = "home-heading";
  h2.id = "productos-destacados-title";
  h2.textContent = "Productos destacados";
  titleWrap.appendChild(eyebrow);
  titleWrap.appendChild(h2);

  const chipsWrap = document.createElement("div");
  chipsWrap.className = "home-featured__filters";
  chipsWrap.setAttribute("role", "group");
  chipsWrap.setAttribute("aria-label", "Filtrar por categoría");

  const grid = document.createElement("div");
  grid.className = "home-featured__grid";

  const renderGrid = (filter: "all" | Product["category"]): void => {
    grid.textContent = "";
    const list = filter === "all" ? allFeatured : allFeatured.filter((p) => p.category === filter);
    for (const p of list) {
      grid.appendChild(buildCard(p));
    }
  };

  const addChip = (label: string, filter: "all" | Product["category"], active: boolean): void => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "filter-chip home-featured__chip";
    if (active) b.classList.add("filter-chip--active");
    b.textContent = label;
    b.addEventListener("click", () => {
      for (const c of chipsWrap.querySelectorAll("button")) {
        c.classList.remove("filter-chip--active");
      }
      b.classList.add("filter-chip--active");
      renderGrid(filter);
    });
    chipsWrap.appendChild(b);
  };

  addChip("Todo", "all", true);
  for (const cat of categoriesInFeatured(allFeatured)) {
    addChip(categoryLabelEs(cat), cat, false);
  }

  head.appendChild(titleWrap);
  head.appendChild(chipsWrap);

  root.appendChild(head);
  root.appendChild(grid);
  renderGrid("all");
}

function buildCard(p: Product): HTMLElement {
  const card = document.createElement("article");
  card.className = "home-featured-card";

  const link = document.createElement("a");
  link.className = "home-featured-card__media-link";
  link.href = `/producto.html?p=${encodeURIComponent(p.slug)}`;

  const figure = document.createElement("figure");
  figure.className = "home-featured-card__media";
  const img = document.createElement("img");
  img.src = imageUrlForKey(p.imageKey);
  img.alt = p.name;
  img.width = 800;
  img.height = 1000;
  img.loading = "lazy";
  img.decoding = "async";
  figure.appendChild(img);

  const shade = document.createElement("div");
  shade.className = "home-featured-card__shade";
  shade.setAttribute("aria-hidden", "true");

  const meta = document.createElement("div");
  meta.className = "home-featured-card__meta";
  const price = document.createElement("span");
  price.className = "home-featured-card__price";
  price.textContent = formatPriceCop(p.priceCop);
  const name = document.createElement("h3");
  name.className = "home-featured-card__name";
  name.textContent = p.name;
  const cat = document.createElement("span");
  cat.className = "home-featured-card__category";
  cat.textContent = categoryLabelEs(p.category);
  meta.appendChild(price);
  meta.appendChild(name);
  meta.appendChild(cat);

  const actions = document.createElement("div");
  actions.className = "home-featured-card__actions";
  const detail = document.createElement("a");
  detail.href = `/producto.html?p=${encodeURIComponent(p.slug)}`;
  detail.className = "btn btn--secondary home-featured-card__btn";
  detail.textContent = "Ver ficha";
  const buy = document.createElement("a");
  buy.href = buildWhatsAppProductUrl(p.name, formatPriceCop(p.priceCop), p.sku);
  buy.target = "_blank";
  buy.rel = "noopener noreferrer";
  buy.className = "btn btn--primary home-featured-card__btn";
  buy.textContent = "Comprar";

  actions.appendChild(detail);
  actions.appendChild(buy);

  link.appendChild(figure);
  link.appendChild(shade);
  link.appendChild(meta);
  card.appendChild(link);
  card.appendChild(actions);
  return card;
}
